import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSwipeable } from "react-swipeable";
import Zoom from "react-medium-image-zoom";

// Animation variants for modal content
const modalParent = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.18,
      delayChildren: 0.1,
    },
  },
};

const modalChild = {
  hidden: { opacity: 0, y: 40, scale: 0.97 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.7,
      type: "spring",
      bounce: 0.3,
    },
  },
};


export default function ProjectModal({ open, onClose, projects, index, setIndex }) {
  const project = projects[index];

  const next = () => {
    if (index < projects.length - 1) setIndex(index + 1);
  };

  const prev = () => {
    if (index > 0) setIndex(index - 1);
  };

  // Swipe Support
  const handlers = useSwipeable({
    onSwipedLeft: next,
    onSwipedRight: prev,
    preventScrollOnSwipe: true,
    trackMouse: true, // allows mouse-drag on desktop
  });

  // Keyboard support
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [index]);

  if (!open || !project) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          onClick={(e) => e.stopPropagation()}
          className="bg-black rounded-2xl shadow-xl overflow-hidden max-w-4xl w-[90%]  flex flex-col sm:flex-row"
          variants={modalParent}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.2 }}
          {...handlers}
        >
          {/* LEFT — ZOOMABLE IMAGE */}
          <motion.div
            className="w-full sm:w-1/2 bg-gray-200 h-72 sm:h-auto flex items-center justify-center"
            variants={modalChild}
          >
            <Zoom>
              <img
                src={project.image ? encodeURI(project.image) : ""}
                alt={project.name}
                className="max-w-full max-h-full object-contain cursor-zoom-in"
              />
            </Zoom>
          </motion.div>

          {/* RIGHT — DETAILS */}
          <motion.div
            className="w-full sm:w-1/2 p-6 flex flex-col"
            variants={modalChild}
          >
            <h2 className="text-2xl font-semibold mb-2 text-white">{project.name}</h2>
            <p className="text-sm text-gray-500 mb-2 text-white">{project.location}</p>

            {project.area && (
              <span className="text-xs bg-[var(--bg3)] text-black-700 px-2 py-1 rounded-full w-fit mb-3">
                {Number(project.area).toLocaleString()} sq.ft
              </span>
            )}


            <div className="mb-6">
              <p className="text-white text-base font-bold mb-1" >
                Project Description :
                <span className="font-normal text-white/70 " >
                  {project.description && (
                    <span style={{whiteSpace:'pre-line'}}> {project.description}</span>
                  )}
                </span>
              </p>
              {project.services && (
                <p className="text-white text-base font-bold mt-4" >
                  Service :
                  <span className="font-normal text-white/70 " >
                    <span style={{whiteSpace:'pre-line'}}> {project.services}</span>
                  </span>
                </p>
              )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-auto">
              <button
                onClick={prev}
                disabled={index === 0}
                className="px-4 py-2 bg-[var(--bg)] text-white rounded-lg disabled:bg-gray-300 disabled:text-gray-500"
              >
                ◀ 
              </button>

              <button
                onClick={next}
                disabled={index === projects.length - 1}
                className="px-4 py-2 bg-[var(--bg)] text-white rounded-lg disabled:bg-gray-300 disabled:text-white/50"
              >
                 ▶
              </button>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
